import { keepPreviousData } from "@tanstack/react-query"
import { useDebounce as useDebouncedValue } from "hooks/use-debounced-value"
import { useSearchTags, useTags } from "sdk/command-palette/queries"

export const PAGE_ID = "tag-search"

export const SHORTCUT = "f t"

const DEBOUNCE_MS = 300

const SEARCH_ENTRY_COMMAND = {
  id: "search-tags",
  label: "Search Tags…",
  icon: "search",
  group: "Search",
  keywords: ["find", "tag", "label"],
  shortcut: SHORTCUT,
  onSelect: api => api.pushPage(PAGE_ID)
}

const toTagCommand = tag => {
  const path = `/subscribers?subscribable_type=Tag&subscribable_ids=${tag.id}`
  return {
    id: `tag-${tag.id}`,
    label: tag.name,
    icon: "tag",
    group: "Tags",
    href: path,
    onSelect: api => api.navigate(path)
  }
}

/**
 * Provider for tag search. At the root it offers the "Search tags…" entry that
 * opens the subpage. On the subpage it preloads the account's recent tags. When
 * that preload holds the whole list (`has_more` is false) we let cmdk filter it
 * client-side, exactly like before. When the account has more tags than the
 * preload returns (`has_more` is true) client-side filtering would miss the
 * rest, so we switch to server-side search — debounced, like subscriber
 * search — and turn cmdk's client filter off. Selecting a tag navigates to the
 * subscribers list filtered by that tag.
 *
 * @type {import("../types").Provider}
 */
export function useTagSearchProvider({ query, activePageId }) {
  const active = activePageId === PAGE_ID
  const trimmedQuery = query.trim()
  const debouncedQuery = useDebouncedValue(trimmedQuery, DEBOUNCE_MS)

  const { data: preloaded, isFetching: isPreloading } = useTags({
    enabled: active
  })
  const hasMore = preloaded?.has_more ?? false
  const serverMode = active && hasMore

  const { data: searchData, isFetching: isSearching } = useSearchTags({
    params: { query: debouncedQuery },
    // Only search once the debounce settles on the current input, and never for
    // an empty term (the preloaded list is the empty-query browse view).
    enabled:
      serverMode &&
      debouncedQuery === trimmedQuery &&
      debouncedQuery.length > 0,
    placeholderData: keepPreviousData
  })

  if (!activePageId) {
    return { commands: [SEARCH_ENTRY_COMMAND], isLoading: false }
  }

  if (!active) return { commands: [], isLoading: false }

  // Server results while actively searching; otherwise the preloaded tags —
  // which are the full list in complete mode and the recent-tags browse list on
  // the empty subpage in server mode.
  const source =
    serverMode && trimmedQuery.length > 0
      ? (searchData?.tags ?? [])
      : (preloaded?.tags ?? [])

  // The moment a term is typed, `source` switches to the (still empty) search
  // results, but the request stays disabled until the debounce settles. Count
  // that gap as loading so the subpage doesn't flash "No tags found." before
  // the search fires.
  const debouncePending =
    serverMode && trimmedQuery.length > 0 && debouncedQuery !== trimmedQuery

  return {
    commands: source.map(toTagCommand),
    isLoading: isPreloading || isSearching || debouncePending,
    shouldFilter: !hasMore
  }
}

/**
 * @returns {import("../types").Shortcut[]}
 */
export function getTagSearchShortcuts() {
  return [{ shortcut: SHORTCUT, onTrigger: api => api.pushPage(PAGE_ID) }]
}
